import { useState, useContext } from "react";
import { Button, Uploader, Message, Panel, Row, Col } from "rsuite";
import { FileType } from "rsuite/esm/Uploader";
import { FileUpload, Trash } from "@rsuite/icons";
import { Link } from "react-router-dom";
import { NotificationContext } from "../../core/context/notificationContextProvider";
import { notificationContextTypes } from "../../utils/typeDefs";
import { postData } from "../../utils/reusables";
import { apiUrls } from "../../utils/apiUrls";
import FileDownloadIcon from "@rsuite/icons/FileDownload";

type MLIDataPageProps = {
  title: string;
};

export const MLIDataPage = ({ title }: MLIDataPageProps) => {
  const [selectedFile, setSelectedFile] = useState<FileType[]>([]);
  const [uploading, setUploading] = useState<boolean>(false);
  const { triggerNotification } = useContext(
    NotificationContext
  ) as notificationContextTypes;

  const handleFileChange = (fileList: FileType[]) => {
    // Only allow single Excel file
    if (fileList.length > 0) {
      const file = fileList[0];
      if (
        file.name &&
        (file.name.endsWith(".xlsx") || file.name.endsWith(".xls"))
      ) {
        setSelectedFile([file]);
      } else {
        triggerNotification(
          false,
          "Please select only Excel files (.xlsx or .xls)"
        );
        setSelectedFile([]);
      }
    } else {
      setSelectedFile([]);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile([]);
  };

  const handleUpload = async () => {
    if (selectedFile.length === 0) {
      triggerNotification(false, "Please select a file to upload");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", selectedFile[0].blobFile as Blob);

    try {
      const response = await postData(apiUrls.mliData.upload, formData);
      console.log("Upload response:", response);
      if (response.status === 200 && response.data.succeeded) {
        triggerNotification(
          true,
          response.data.message || "File uploaded successfully!"
        );
        setSelectedFile([]); // Reset to previous state
      } else {
        triggerNotification(
          false,
          response.data.message || "Failed to upload file"
        );
      }
    } catch (error: any) {
      const errorMessage = error.message || "Unknown error occurred";
      triggerNotification(false, "Error uploading file: " + errorMessage);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="content-box-title">
      <h1 className="page-title pb-3">{title}</h1>

      <Panel bordered style={{ marginBottom: "20px" }}>
        <Row gutter={16}>
          {" "}
          {/* File Upload Section - Left Side */}
          <Col xs={24} sm={12} md={14}>
            <div
              style={{
                padding: "20px",
                backgroundColor: "#f1f8ff",
                borderRadius: "8px",
                minHeight: "300px",
              }}
            >
              <h5 style={{ marginBottom: "15px", color: "#088abd" }}>
                Upload Excel File
              </h5>

              {selectedFile.length === 0 ? (
                <Uploader
                  fileList={selectedFile}
                  onChange={handleFileChange}
                  autoUpload={false}
                  multiple={false}
                  accept=".xlsx,.xls"
                  draggable
                  disabled={uploading}
                  style={{
                    border: "2px dashed #088abd",
                    borderRadius: "8px",
                    padding: "40px 20px",
                    textAlign: "center",
                    backgroundColor: "#ffffff",
                  }}
                  action={""}
                >
                  <div
                    style={{
                      height: "80px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    {" "}
                    <FileUpload
                      style={{
                        fontSize: "32px",
                        color: "#088abd",
                        marginBottom: "10px",
                      }}
                    />
                    <p style={{ margin: 0, color: "#555a6b" }}>
                      Drag & drop your Excel file here or{" "}
                      <strong style={{ color: "#088abd" }}>browse</strong>
                    </p>
                    <p
                      style={{
                        margin: "5px 0 0 0",
                        fontSize: "12px",
                        color: "#999",
                      }}
                    >
                      Only .xlsx and .xls files are supported
                    </p>
                  </div>
                </Uploader>
              ) : (
                <div
                  style={{
                    border: "1px solid #e5e5e5",
                    borderRadius: "8px",
                    padding: "15px",
                    backgroundColor: "#fff",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    {" "}
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <FileUpload
                        style={{ color: "#26a69a", marginRight: "10px" }}
                      />
                      <div>
                        <strong>{selectedFile[0].name}</strong>
                        <p
                          style={{
                            margin: 0,
                            fontSize: "12px",
                            color: "#555a6b",
                          }}
                        >
                          Ready to upload
                        </p>
                      </div>
                    </div>
                    <Button
                      appearance="subtle"
                      size="sm"
                      onClick={handleRemoveFile}
                      disabled={uploading}
                    >
                      <Trash style={{ color: "#dc3545" }} />
                    </Button>
                  </div>{" "}
                  <Button
                    appearance="primary"
                    style={{
                      marginTop: "15px",
                      width: "100%",
                      backgroundColor: "#088abd",
                      borderColor: "#088abd",
                    }}
                    loading={uploading}
                    onClick={handleUpload}
                  >
                    {uploading ? "Publishing..." : "Publish"}
                  </Button>
                </div>
              )}
            </div>
          </Col>{" "}
          {/* Download Template Section - Right Side */}
          <Col xs={24} sm={12} md={10}>
            <div
              style={{
                padding: "20px",
                backgroundColor: "#25384b",
                borderRadius: "8px",
                height: "100%",
                minHeight: "300px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <h5 style={{ marginBottom: "15px", color: "#23b3f2" }}>
                Download Template
              </h5>
              <p style={{ color: "#b0bec5", marginBottom: "20px" }}>
                Download the MLI Information template to get started. Fill in
                the required fields and upload your completed file.
              </p>
              <Link
                to="/files/MLI-Information.xlsx"
                target="_blank"
                download
                style={{ textDecoration: "none" }}
              >
                <Button
                  appearance="ghost"
                  style={{
                    borderColor: "#23b3f2",
                    color: "#23b3f2",
                    width: "100%",
                  }}
                >
                  <FileDownloadIcon style={{ marginRight: "8px" }} />
                  Download Template
                </Button>
              </Link>
            </div>
          </Col>
        </Row>
      </Panel>

      <Message type="info" style={{ marginTop: "20px" }}>
        <strong>Instructions:</strong>
        <ul style={{ marginTop: "10px", paddingLeft: "20px" }}>
          <li>
            Download the <b>MLI-Information.xlsx</b> template file first to
            understand the required format
          </li>
          <li>Fill in your informations according to the template structure</li>
          <li>Save your file in Excel format (.xlsx or .xls)</li>
          <li>Upload your completed file using the uploader above</li>
        </ul>
      </Message>
    </div>
  );
};

export default MLIDataPage;
